/**
 * disable-pinch-zoom.ts
 *
 * iOS Safari has ignored `user-scalable=no` and `maximum-scale=1.0` in the
 * viewport meta since iOS 10 (apple.com/library/releasenotes Safari_10_0).
 * The only reliable client-side approach is to intercept gesture events on
 * the document and call preventDefault().
 *
 * Accessibility trade-off: the operator has explicitly authorised this for
 * proof.'s workspace. Rationale: the workspace is a code-editor-adjacent
 * surface where accidental pinch-zoom severely disrupts the interface (the
 * editor and the fixed bottom activity bar drift apart, annotations become
 * unreadable). Intentional zoom via Settings > Accessibility is unaffected
 * at the OS level.
 *
 * This module is imported as a side effect from entry.client.tsx and runs
 * before the first React render.
 *
 * Android Chrome / desktop: the viewport meta `maximum-scale=1.0,
 * user-scalable=no` (in index.html) handles those platforms; this file
 * handles iOS Safari specifically.
 */

function preventDefault(e: Event) {
  e.preventDefault();
}

/**
 * Attach gesture event listeners that block iOS Safari pinch-zoom.
 * Idempotent: safe to call multiple times (the handler reference is
 * stable so duplicate addEventListener calls are deduped by the browser).
 */
export function disablePinchZoom(): void {
  // gesturestart / gesturechange / gestureend are proprietary iOS Safari events
  // that fire on multi-touch gestures. Calling preventDefault() on them stops
  // the zoom scale from changing.
  document.addEventListener("gesturestart", preventDefault, { passive: false });
  document.addEventListener("gesturechange", preventDefault, { passive: false });
  document.addEventListener("gestureend", preventDefault, { passive: false });
}

// Run immediately as a side-effect when this module is imported.
disablePinchZoom();
